import { Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  MapPin,
  Plus,
  Shield,
  SlidersHorizontal,
  User,
} from "lucide-react";
import { useMemo, useState } from "react";
import { FieldOpsFilterRow } from "@/components/field-ops/field-ops-filters";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useProjects } from "@/hooks/use-projects";
import {
  type CreateSafetyIncidentInput,
  type InjurySeverity,
  type SafetyIncident,
  type SafetyIncidentFilters,
  useCreateSafetyIncident,
  useSafetyIncidents,
} from "@/hooks/use-safety-incidents";
import { parseIncidentNarrative } from "@/lib/safety-incident-narrative";
import { cn } from "@/lib/utils";

const SEVERITY_LABELS: Record<InjurySeverity, string> = {
  none: "None",
  first_aid: "First Aid",
  recordable: "Recordable",
  lost_time: "Lost Time",
  fatal: "Fatal",
};

const SEVERITY_ORDER: InjurySeverity[] = ["none", "first_aid", "recordable", "lost_time", "fatal"];

const SEVERITY_FILTER_OPTIONS: { label: string; value: string }[] = [
  { label: "All severities", value: "all" },
  ...SEVERITY_ORDER.map((s) => ({ label: SEVERITY_LABELS[s], value: s })),
];

const CONTRIBUTING_FACTORS = [
  "Falls",
  "Struck by object",
  "Electrical",
  "Chemical exposure",
  "Overexertion",
  "Equipment failure",
  "Other",
];

function severityBadgeClass(severity: InjurySeverity): string {
  switch (severity) {
    case "fatal":
      return "bg-destructive text-destructive-foreground";
    case "lost_time":
      return "bg-orange-500 text-white";
    case "recordable":
      return "bg-yellow-500 text-white";
    case "first_aid":
      return "bg-blue-500 text-white";
    default:
      return "bg-secondary text-secondary-foreground";
  }
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function isWithinLast30Days(dateStr: string): boolean {
  const ms = Date.now() - new Date(dateStr).getTime();
  return ms <= 30 * 24 * 60 * 60 * 1000;
}

interface SeverityBadgeProps {
  severity: InjurySeverity;
  size?: "sm" | "default";
}

function SeverityBadge({ severity, size = "default" }: SeverityBadgeProps) {
  return (
    <span
      className={`inline-flex items-center rounded-full font-medium ${severityBadgeClass(severity)} ${size === "sm" ? "px-2 py-0.5 text-xs" : "px-2.5 py-1 text-xs"}`}
    >
      {SEVERITY_LABELS[severity]}
    </span>
  );
}

interface IncidentCardProps {
  incident: SafetyIncident;
}

function IncidentCard({ incident }: IncidentCardProps) {
  const openActions = incident.correctiveActions.filter((a) => a.completedAt === null).length;
  const totalActions = incident.correctiveActions.length;
  const narrative = parseIncidentNarrative(incident.description);
  const personsInvolved = narrative.personsInvolved ?? incident.personsInvolved ?? undefined;
  const contributingFactors =
    narrative.contributingFactors ?? incident.contributingFactors ?? undefined;
  const witnessStatements = narrative.witnessStatements ?? incident.witnessStatements ?? undefined;

  return (
    <Link to="/safety-incidents/$id" params={{ id: incident.id }} className="block">
      <Card className="gap-0 py-0 shadow-sm hover:bg-muted/50 transition-colors cursor-pointer">
        <CardContent className="px-3 py-2 sm:px-4 sm:py-2.5">
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1.5">
                <SeverityBadge severity={incident.injurySeverity} size="sm" />
                <Badge
                  variant={incident.status === "open" ? "default" : "secondary"}
                  className="text-xs"
                >
                  {incident.status === "open" ? "Open" : "Closed"}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  #{incident.incidentNumber} · {formatDate(incident.incidentDate)}
                </span>
              </div>
              <div className="space-y-1 min-w-0">
                <p className="text-sm font-medium text-foreground line-clamp-2">{narrative.main}</p>
                {personsInvolved ? (
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    <span className="font-medium text-foreground/85">Persons involved</span>
                    {": "}
                    {personsInvolved}
                  </p>
                ) : null}
                {contributingFactors ? (
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    <span className="font-medium text-foreground/85">Contributing</span>
                    {": "}
                    {contributingFactors}
                  </p>
                ) : null}
                {witnessStatements ? (
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    <span className="font-medium text-foreground/85">Witnesses</span>
                    {": "}
                    {witnessStatements}
                  </p>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {incident.location}
                </span>
                {incident.projectName && (
                  <span className="text-muted-foreground">{incident.projectName}</span>
                )}
                {incident.createdByName && (
                  <span className="flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {incident.createdByName}
                  </span>
                )}
                {totalActions > 0 && (
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" />
                    {openActions} open / {totalActions} actions
                  </span>
                )}
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

type SafetyIncidentsFilterProps = {
  filterProjectId: string;
  setFilterProjectId: (v: string) => void;
  dateFrom: string;
  setDateFrom: (v: string) => void;
  dateTo: string;
  setDateTo: (v: string) => void;
  severityFilter: string;
  setSeverityFilter: (v: string) => void;
  sortedProjects: Array<{ id: string; name: string }>;
};

function buildSafetyIncidentActiveFilterLabels(props: SafetyIncidentsFilterProps): string[] {
  const labels: string[] = [];
  if (props.filterProjectId) {
    const project = props.sortedProjects.find((p) => p.id === props.filterProjectId);
    labels.push(project?.name ?? "Project");
  }
  if (props.dateFrom) labels.push(`From ${formatDate(props.dateFrom)}`);
  if (props.dateTo) labels.push(`To ${formatDate(props.dateTo)}`);
  if (props.severityFilter !== "all") {
    labels.push(SEVERITY_LABELS[props.severityFilter as InjurySeverity] ?? props.severityFilter);
  }
  return labels;
}

function SafetyIncidentsFilterFields({
  idPrefix,
  ...props
}: SafetyIncidentsFilterProps & { idPrefix: string }) {
  return (
    <>
      <FieldOpsFilterRow label="Project" htmlFor={`${idPrefix}-project`}>
        <Select
          value={props.filterProjectId || "all"}
          onValueChange={(v) => props.setFilterProjectId(v === "all" ? "" : v)}
        >
          <SelectTrigger id={`${idPrefix}-project`} className="min-h-[44px] w-full">
            <SelectValue placeholder="All projects" />
          </SelectTrigger>
          <SelectContent position="popper" sideOffset={4}>
            <SelectItem value="all">All projects</SelectItem>
            {props.sortedProjects.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldOpsFilterRow>

      <FieldOpsFilterRow label="From" htmlFor={`${idPrefix}-from`}>
        <Input
          id={`${idPrefix}-from`}
          type="date"
          value={props.dateFrom}
          onChange={(e) => props.setDateFrom(e.target.value)}
          className="min-h-[44px]"
        />
      </FieldOpsFilterRow>

      <FieldOpsFilterRow label="To" htmlFor={`${idPrefix}-to`}>
        <Input
          id={`${idPrefix}-to`}
          type="date"
          value={props.dateTo}
          onChange={(e) => props.setDateTo(e.target.value)}
          className="min-h-[44px]"
        />
      </FieldOpsFilterRow>

      <FieldOpsFilterRow label="Severity" htmlFor={`${idPrefix}-severity`}>
        <Select value={props.severityFilter} onValueChange={props.setSeverityFilter}>
          <SelectTrigger id={`${idPrefix}-severity`} className="min-h-[44px] w-full">
            <SelectValue placeholder="All severities" />
          </SelectTrigger>
          <SelectContent position="popper" sideOffset={4}>
            {SEVERITY_FILTER_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldOpsFilterRow>
    </>
  );
}

function SafetyIncidentsMobileFilters(props: SafetyIncidentsFilterProps) {
  const [open, setOpen] = useState(false);
  const activeLabels = buildSafetyIncidentActiveFilterLabels(props);
  const activeCount = activeLabels.length;

  function clearFilters() {
    props.setFilterProjectId("");
    props.setDateFrom("");
    props.setDateTo("");
    props.setSeverityFilter("all");
  }

  return (
    <Card className="gap-0 py-0 shadow-sm overflow-hidden">
      <button
        type="button"
        className="flex w-full items-center gap-3 px-3 py-3 min-h-[44px] text-left sm:px-4"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="si-mobile-filters-panel"
      >
        <SlidersHorizontal className="h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Filters</span>
            {activeCount > 0 && (
              <Badge variant="secondary" className="text-xs">
                {activeCount}
              </Badge>
            )}
          </div>
          {!open && activeCount > 0 && (
            <p className="text-xs text-muted-foreground truncate mt-0.5">
              {activeLabels.join(" · ")}
            </p>
          )}
          {!open && activeCount === 0 && (
            <p className="text-xs text-muted-foreground mt-0.5">Tap to refine the list</p>
          )}
        </div>
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180",
          )}
        />
      </button>

      {open && (
        <CardContent
          id="si-mobile-filters-panel"
          className="space-y-2 border-t px-3 py-2 sm:px-4 sm:py-2.5"
        >
          <SafetyIncidentsFilterFields idPrefix="si-filter" {...props} />
          {activeCount > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full min-h-[44px] text-muted-foreground"
              onClick={clearFilters}
            >
              Clear all filters
            </Button>
          )}
        </CardContent>
      )}
    </Card>
  );
}

function SafetyIncidentsDesktopFilters(props: SafetyIncidentsFilterProps) {
  const activeCount = buildSafetyIncidentActiveFilterLabels(props).length;

  function clearFilters() {
    props.setFilterProjectId("");
    props.setDateFrom("");
    props.setDateTo("");
    props.setSeverityFilter("all");
  }

  return (
    <Card className="gap-0 py-0 shadow-sm">
      <CardContent className="px-4 py-3">
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          <div className="space-y-1.5 min-w-0">
            <Label htmlFor="si-dt-project" className="text-xs text-muted-foreground">
              Project
            </Label>
            <Select
              value={props.filterProjectId || "all"}
              onValueChange={(v) => props.setFilterProjectId(v === "all" ? "" : v)}
            >
              <SelectTrigger id="si-dt-project" className="h-9 w-full">
                <SelectValue placeholder="All projects" />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={4}>
                <SelectItem value="all">All projects</SelectItem>
                {props.sortedProjects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 min-w-0">
            <Label htmlFor="si-dt-from" className="text-xs text-muted-foreground">
              From
            </Label>
            <Input
              id="si-dt-from"
              type="date"
              value={props.dateFrom}
              onChange={(e) => props.setDateFrom(e.target.value)}
              className="h-9"
            />
          </div>
          <div className="space-y-1.5 min-w-0">
            <Label htmlFor="si-dt-to" className="text-xs text-muted-foreground">
              To
            </Label>
            <Input
              id="si-dt-to"
              type="date"
              value={props.dateTo}
              onChange={(e) => props.setDateTo(e.target.value)}
              className="h-9"
            />
          </div>
          <div className="flex items-end gap-3">
            <div className="space-y-1.5 min-w-0 flex-1">
              <Label htmlFor="si-dt-severity" className="text-xs text-muted-foreground">
                Severity
              </Label>
              <Select value={props.severityFilter} onValueChange={props.setSeverityFilter}>
                <SelectTrigger id="si-dt-severity" className="h-9 w-full">
                  <SelectValue placeholder="All severities" />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={4}>
                  {SEVERITY_FILTER_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {activeCount > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-9 shrink-0"
                onClick={clearFilters}
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SafetyIncidentsDesktopTableRow({ incident }: { incident: SafetyIncident }) {
  const openActions = incident.correctiveActions.filter((a) => a.completedAt === null).length;
  const totalActions = incident.correctiveActions.length;
  const narrative = parseIncidentNarrative(incident.description);

  return (
    <TableRow className="hover:bg-muted/50">
      <TableCell className="py-2.5 font-mono text-xs text-muted-foreground whitespace-nowrap">
        <Link to="/safety-incidents/$id" params={{ id: incident.id }} className="hover:underline">
          #{incident.incidentNumber}
        </Link>
      </TableCell>
      <TableCell className="py-2.5 whitespace-nowrap text-sm">
        <Link to="/safety-incidents/$id" params={{ id: incident.id }} className="hover:underline">
          {formatDate(incident.incidentDate)}
        </Link>
      </TableCell>
      <TableCell className="py-2.5 max-w-[140px] truncate text-muted-foreground text-sm">
        {incident.projectName ?? "—"}
      </TableCell>
      <TableCell className="py-2.5 max-w-[120px] truncate text-muted-foreground text-sm">
        {incident.location}
      </TableCell>
      <TableCell className="py-2.5">
        <SeverityBadge severity={incident.injurySeverity} size="sm" />
      </TableCell>
      <TableCell className="py-2.5">
        <Badge variant={incident.status === "open" ? "default" : "secondary"} className="text-xs">
          {incident.status === "open" ? "Open" : "Closed"}
        </Badge>
      </TableCell>
      <TableCell className="py-2.5 text-sm text-muted-foreground whitespace-nowrap">
        {totalActions > 0 ? `${openActions} open / ${totalActions}` : "—"}
      </TableCell>
      <TableCell className="py-2.5 max-w-[240px]">
        <Link
          to="/safety-incidents/$id"
          params={{ id: incident.id }}
          className="line-clamp-2 text-sm hover:underline"
        >
          {narrative.main}
        </Link>
      </TableCell>
    </TableRow>
  );
}

function SafetyIncidentsDesktopTable({ incidents }: { incidents: SafetyIncident[] }) {
  const sorted = [...incidents].sort((a, b) => b.incidentDate.localeCompare(a.incidentDate));

  return (
    <Card className="gap-0 py-0 shadow-sm overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-16">#</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Project</TableHead>
            <TableHead>Location</TableHead>
            <TableHead>Severity</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Actions</TableHead>
            <TableHead>Description</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((incident) => (
            <SafetyIncidentsDesktopTableRow key={incident.id} incident={incident} />
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

interface NewIncidentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function NewIncidentDialog({ open, onOpenChange }: NewIncidentDialogProps) {
  const [step, setStep] = useState<1 | 2>(1);
  const [projectId, setProjectId] = useState("");
  const [incidentDate, setIncidentDate] = useState("");
  const [incidentTime, setIncidentTime] = useState("");
  const [location, setLocation] = useState("");
  const [severity, setSeverity] = useState<InjurySeverity>("none");
  const [description, setDescription] = useState("");
  const [personsInvolved, setPersonsInvolved] = useState("");
  const [selectedFactors, setSelectedFactors] = useState<string[]>([]);
  const [witnessStatements, setWitnessStatements] = useState("");

  const create = useCreateSafetyIncident();
  const { data: projects = [], isLoading: projectsLoading } = useProjects();

  function toggleFactor(factor: string) {
    setSelectedFactors((prev) =>
      prev.includes(factor) ? prev.filter((f) => f !== factor) : [...prev, factor],
    );
  }

  function resetForm() {
    setStep(1);
    setProjectId("");
    setIncidentDate("");
    setIncidentTime("");
    setLocation("");
    setSeverity("none");
    setDescription("");
    setPersonsInvolved("");
    setSelectedFactors([]);
    setWitnessStatements("");
  }

  function handleOpenChange(next: boolean) {
    if (!next) resetForm();
    onOpenChange(next);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!projectId || !incidentDate || !location.trim() || !description.trim()) return;

    const input: CreateSafetyIncidentInput = {
      projectId,
      incidentDate,
      location: location.trim(),
      injurySeverity: severity,
      description: description.trim(),
    };
    if (incidentTime) input.incidentTime = incidentTime;
    if (personsInvolved.trim()) input.personsInvolved = personsInvolved.trim();
    if (selectedFactors.length > 0) input.contributingFactors = selectedFactors.join(", ");
    if (witnessStatements.trim()) input.witnessStatements = witnessStatements.trim();

    create.mutate(input, {
      onSuccess: () => {
        handleOpenChange(false);
      },
    });
  }

  const step1Valid =
    projectId.length > 0 &&
    !projectsLoading &&
    incidentDate.length > 0 &&
    location.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Report Incident — Step {step} of 2</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          {step === 1 && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="si-date">Date *</Label>
                  <Input
                    id="si-date"
                    type="date"
                    value={incidentDate}
                    onChange={(e) => setIncidentDate(e.target.value)}
                    required
                    className="min-h-[44px]"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="si-time">Time</Label>
                  <Input
                    id="si-time"
                    type="time"
                    value={incidentTime}
                    onChange={(e) => setIncidentTime(e.target.value)}
                    className="min-h-[44px]"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="si-location">Location *</Label>
                <Input
                  id="si-location"
                  placeholder="e.g. Level 3, East stairwell"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  required
                  className="min-h-[44px]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="si-severity">Injury Severity *</Label>
                <Select value={severity} onValueChange={(v) => setSeverity(v as InjurySeverity)}>
                  <SelectTrigger id="si-severity" className="min-h-[44px] w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={4}>
                    {SEVERITY_ORDER.map((s) => (
                      <SelectItem key={s} value={s}>
                        {SEVERITY_LABELS[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="si-project">Project *</Label>
                <Select
                  value={projectId || undefined}
                  onValueChange={setProjectId}
                  disabled={projectsLoading}
                >
                  <SelectTrigger id="si-project" className="min-h-[44px] w-full">
                    <SelectValue
                      placeholder={projectsLoading ? "Loading projects…" : "Select project"}
                    />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={4}>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="si-description">Description *</Label>
                <Textarea
                  id="si-description"
                  placeholder="Describe what happened"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="si-persons">Persons Involved</Label>
                <Textarea
                  id="si-persons"
                  placeholder="Names and roles of those involved"
                  value={personsInvolved}
                  onChange={(e) => setPersonsInvolved(e.target.value)}
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Contributing Factors</Label>
                <div className="grid grid-cols-2 gap-2">
                  {CONTRIBUTING_FACTORS.map((factor) => (
                    <div key={factor} className="flex items-center gap-2 min-h-[44px]">
                      <Checkbox
                        id={`factor-${factor}`}
                        checked={selectedFactors.includes(factor)}
                        onCheckedChange={() => toggleFactor(factor)}
                      />
                      <Label htmlFor={`factor-${factor}`} className="font-normal cursor-pointer">
                        {factor}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="si-witness">Witness Statements</Label>
                <Textarea
                  id="si-witness"
                  placeholder="Statements from witnesses"
                  value={witnessStatements}
                  onChange={(e) => setWitnessStatements(e.target.value)}
                  rows={2}
                />
              </div>
            </div>
          )}

          <DialogFooter className="mt-6 gap-2">
            {step === 1 && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  className="min-h-[44px]"
                  onClick={() => handleOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  className="min-h-[44px]"
                  disabled={!step1Valid}
                  onClick={() => setStep(2)}
                >
                  Next
                </Button>
              </>
            )}
            {step === 2 && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  className="min-h-[44px]"
                  onClick={() => setStep(1)}
                  disabled={create.isPending}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  className="min-h-[44px]"
                  disabled={create.isPending || !description.trim()}
                >
                  {create.isPending ? "Submitting…" : "Submit Report"}
                </Button>
              </>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function SafetyIncidentsPage() {
  const [severityFilter, setSeverityFilter] = useState<string>("all");
  const [filterProjectId, setFilterProjectId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [newDialogOpen, setNewDialogOpen] = useState(false);

  const listFilters = useMemo((): SafetyIncidentFilters | undefined => {
    const f: SafetyIncidentFilters = {};
    if (severityFilter !== "all") f.severity = severityFilter;
    if (filterProjectId) f.projectId = filterProjectId;
    if (dateFrom) f.dateFrom = dateFrom;
    if (dateTo) f.dateTo = dateTo;
    return Object.keys(f).length > 0 ? f : undefined;
  }, [severityFilter, filterProjectId, dateFrom, dateTo]);

  const { data, isLoading, isError, error, refetch } = useSafetyIncidents(listFilters);

  const { data: projects = [] } = useProjects();
  const sortedProjects = useMemo(
    () => [...projects].sort((a, b) => a.name.localeCompare(b.name)),
    [projects],
  );

  const incidents = data ?? [];

  const hasCriticalRecent = incidents.some(
    (i) =>
      (i.injurySeverity === "fatal" || i.injurySeverity === "lost_time") &&
      isWithinLast30Days(i.incidentDate),
  );

  const filterProps: SafetyIncidentsFilterProps = {
    filterProjectId,
    setFilterProjectId,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    severityFilter,
    setSeverityFilter,
    sortedProjects,
  };

  return (
    <div className="space-y-4 lg:space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">Safety Incidents</h1>
        <div className="flex items-center gap-2">
          <Button className="min-h-[44px] gap-2 lg:min-h-9" onClick={() => setNewDialogOpen(true)}>
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Report Incident</span>
          </Button>
        </div>
      </div>

      {hasCriticalRecent && (
        <div className="flex items-start gap-3 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3">
          <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
          <p className="text-sm text-destructive font-medium">
            One or more fatal or lost-time injury incidents were reported in the last 30 days.
            Ensure internal review and any required external reporting follow your company
            procedure.
          </p>
        </div>
      )}

      <div className="hidden lg:block">
        <SafetyIncidentsDesktopFilters {...filterProps} />
      </div>

      <div className="lg:hidden">
        <SafetyIncidentsMobileFilters {...filterProps} />
      </div>

      {isLoading && (
        <>
          <div className="space-y-2 lg:hidden">
            {(["s1", "s2", "s3", "s4"] as const).map((k) => (
              <Skeleton key={k} className="h-24 w-full rounded-lg" />
            ))}
          </div>
          <div className="hidden lg:block">
            <Skeleton className="h-80 w-full rounded-lg" />
          </div>
        </>
      )}

      {isError && (
        <ErrorState
          message={error instanceof Error ? error.message : "Failed to load incidents"}
          onRetry={() => void refetch()}
        />
      )}

      {!isLoading && !isError && incidents.length === 0 && (
        <Card>
          <CardContent className="py-16 flex flex-col items-center gap-4 text-center">
            <Shield className="h-12 w-12 text-muted-foreground" />
            <div>
              <p className="font-semibold text-lg">No incidents reported</p>
              <p className="text-sm text-muted-foreground mt-1">Keep it that way.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {!isLoading && !isError && incidents.length > 0 && (
        <>
          <div className="space-y-2 lg:hidden">
            {incidents.map((incident) => (
              <IncidentCard key={incident.id} incident={incident} />
            ))}
          </div>
          <div className="hidden lg:block">
            <SafetyIncidentsDesktopTable incidents={incidents} />
          </div>
        </>
      )}

      <NewIncidentDialog open={newDialogOpen} onOpenChange={setNewDialogOpen} />
    </div>
  );
}
